import { useState } from "react";
import { Trash2, Flame, Weight, Ruler, Wrench, Boxes, GitCompare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SpecRow } from "./MaterialCatalog";

const MaterialToggle = ({ materials, value, onChange }) => (
  <div className="grid grid-cols-2 gap-2" data-testid="material-toggle-group">
    {materials.map((m) => (
      <button
        key={m.id}
        type="button"
        data-testid={`material-toggle-${m.id}`}
        onClick={() => onChange(m.id)}
        className={`flex items-center gap-2 rounded-md border px-2.5 py-2 text-left text-xs transition-colors duration-200 ${value === m.id ? "border-sky-500 bg-sky-500/10 text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white"}`}
      >
        <span className="h-3 w-3 shrink-0 rounded-sm" style={{ background: m.color }} />
        {m.short}
      </button>
    ))}
  </div>
);

const Num = ({ label, value, onChange, testid }) => (
  <label className="space-y-1">
    <span className="font-mono text-[10px] uppercase text-zinc-500">{label}</span>
    <Input data-testid={testid} type="number" step="0.5" value={Math.round(value * 10) / 10} onChange={(e) => onChange(Number(e.target.value))} className="h-8 border-zinc-800 bg-zinc-900 px-2 font-mono text-xs" />
  </label>
);

const ZoneToSystem = ({ zone, size, onCreate, onCompare }) => {
  const [type, setType] = useState("curtain_wall");
  const [brand, setBrand] = useState("bhoruka");
  const [busy, setBusy] = useState(false);
  if (!size) return <p className="rounded-md border border-dashed border-zinc-800 p-3 text-xs text-zinc-500" data-testid="zone-scale-hint">Set the drawing scale to see real zone sizes and turn this zone into a sized system.</p>;
  return (
    <div className="space-y-2 rounded-md border border-sky-500/30 bg-sky-500/5 p-3" data-testid="zone-to-system">
      <div className="font-mono text-xs text-zinc-300" data-testid="zone-real-size">{size.w.toFixed(2)} m × {size.h.toFixed(2)} m · {(size.w * size.h).toFixed(1)} m²</div>
      <div className="flex gap-2">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="zone-system-type-select" className="h-8 border-zinc-800 bg-zinc-900 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="curtain_wall">Curtain wall</SelectItem>
            <SelectItem value="skylight">Skylight</SelectItem>
            <SelectItem value="canopy">Canopy</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="zone-create-system-btn" size="sm" disabled={busy} onClick={async () => { setBusy(true); await onCreate(zone, type, type === "curtain_wall" ? brand : "bhoruka"); setBusy(false); }} className="shrink-0 bg-sky-500 text-white hover:bg-sky-600">
          <Boxes className="mr-1.5 h-3.5 w-3.5" />Create system
        </Button>
      </div>
      {type === "curtain_wall" && (
        <div className="flex gap-2">
          <Select value={brand} onValueChange={setBrand}>
            <SelectTrigger data-testid="zone-brand-select" className="h-8 border-zinc-800 bg-zinc-900 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="bhoruka">Bhoruka profiles</SelectItem>
              <SelectItem value="bway">BWAY Slimpro</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="zone-compare-btn" size="sm" variant="outline" onClick={() => onCompare(zone)} className="shrink-0 border-zinc-700 text-xs hover:text-white">
            <GitCompare className="mr-1.5 h-3.5 w-3.5" />BWAY vs Bhoruka
          </Button>
        </div>
      )}
    </div>
  );
};

export default function MaterialInspector({ zone, materials, onChange, onDelete, size, onCreateSystem, onCompare }) {
  if (!zone) {
    return (
      <div data-testid="inspector-empty" className="rounded-lg border border-dashed border-zinc-800 p-6 text-sm text-zinc-500">
        Select a zone on the elevation, or turn on <span className="text-amber-400">Draw zone</span> and drag a box to add one.
      </div>
    );
  }
  const mat = materials.find((m) => m.id === zone.material_id);
  const set = (k) => (v) => onChange({ ...zone, [k]: v });
  const setMaterial = (id) => {
    const m = materials.find((x) => x.id === id);
    onChange({ ...zone, material_id: id, thickness_mm: m.default_thickness, finish: m.finishes[0] });
  };

  return (
    <div className="space-y-5 rounded-lg border border-zinc-800 bg-[#121215] p-5" data-testid="material-inspector">
      <div className="flex items-center justify-between gap-2">
        <Input data-testid="zone-label-input" value={zone.label} onChange={(e) => set("label")(e.target.value)} className="h-9 border-zinc-800 bg-zinc-900 font-[Manrope] font-semibold" />
        <button data-testid="delete-zone-btn" onClick={() => onDelete(zone.id)} className="rounded-md border border-zinc-800 p-2 text-zinc-500 hover:border-red-500/50 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div>
        <div className="mb-2 font-mono text-[11px] uppercase tracking-wider text-zinc-500">Material</div>
        <MaterialToggle materials={materials} value={zone.material_id} onChange={setMaterial} />
      </div>
      {mat && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <span className="font-mono text-[10px] uppercase text-zinc-500">Thickness</span>
              <Select value={String(zone.thickness_mm ?? mat.default_thickness)} onValueChange={(v) => set("thickness_mm")(Number(v))}>
                <SelectTrigger data-testid="zone-thickness-select" className="h-8 border-zinc-800 bg-zinc-900 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{mat.thickness_mm.map((t) => <SelectItem key={t} value={String(t)}>{t} mm</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <span className="font-mono text-[10px] uppercase text-zinc-500">Finish</span>
              <Select value={zone.finish || mat.finishes[0]} onValueChange={set("finish")}>
                <SelectTrigger data-testid="zone-finish-select" className="h-8 border-zinc-800 bg-zinc-900 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{mat.finishes.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2 rounded-md border border-zinc-800 p-3" data-testid="zone-material-specs">
            <SpecRow icon={Weight} label="Weight" value={`${mat.weight_kg_m2} kg/m²`} />
            <SpecRow icon={Flame} label="Fire" value={mat.fire_class} />
            <SpecRow icon={Ruler} label="Max panel" value={`${mat.max_panel_mm} mm`} />
            <SpecRow icon={Wrench} label="Subframe" value={mat.subframe} />
          </div>
        </>
      )}
      <ZoneToSystem zone={zone} size={size} onCreate={onCreateSystem} onCompare={onCompare} />
      <div className="grid grid-cols-4 gap-2">
        <Num label="X %" value={zone.x} onChange={set("x")} testid="zone-x-input" />
        <Num label="Y %" value={zone.y} onChange={set("y")} testid="zone-y-input" />
        <Num label="W %" value={zone.w} onChange={set("w")} testid="zone-w-input" />
        <Num label="H %" value={zone.h} onChange={set("h")} testid="zone-h-input" />
      </div>
      <Textarea data-testid="zone-notes-input" value={zone.notes || ""} onChange={(e) => set("notes")(e.target.value)} placeholder="Notes" className="min-h-[70px] border-zinc-800 bg-zinc-900 text-sm" />
    </div>
  );
}
